import { getRuntimeEnvironmentFromProjectId, isSupportedRuntimeEnvironment } from '#shared/constants/firebase'

declare global {
    interface Window {
        exponea: any
        $exponea?: any
        webxpClient?: {
            reset?: () => void
        }
    }
}

const noop = () => {}

type ExponeaBootstrapConfig = {
    target: string
    token: string
    track: {
        visits: boolean
    }
    file_path?: string
    new_experiments?: boolean
    non_flickering_blocking_script?: boolean
    [key: string]: unknown
}

type ExponeaQueue = {
    _: Array<[string, IArguments]>
    [key: string]: any
}

type ExponeaWindow = Window & Record<string, any>

type ExponeaMethodName =
    | 'anonymize'
    | 'initialize'
    | 'identify'
    | 'update'
    | 'track'
    | 'trackLink'
    | 'trackEnhancedEcommerce'
    | 'getHtml'
    | 'showHtml'
    | 'showBanner'
    | 'showWebLayer'
    | 'ping'
    | 'getAbTest'
    | 'loadDependency'
    | 'getRecommendation'
    | 'reloadWebLayers'
    | 'start'

type ExponeaNotificationMethodName = 'isAvailable' | 'isSubscribed' | 'subscribe' | 'unsubscribe'

const EXPONEA_METHODS: readonly ExponeaMethodName[] = [
    'anonymize',
    'initialize',
    'identify',
    'update',
    'track',
    'trackLink',
    'trackEnhancedEcommerce',
    'getHtml',
    'showHtml',
    'showBanner',
    'showWebLayer',
    'ping',
    'getAbTest',
    'loadDependency',
    'getRecommendation',
    'reloadWebLayers',
    'start'
] as const

const EXPONEA_NOTIFICATION_METHODS: readonly ExponeaNotificationMethodName[] = [
    'isAvailable',
    'isSubscribed',
    'subscribe',
    'unsubscribe'
] as const

const createExponeaStub = () => ({
    getSDK: () => null,
    anonymize: noop,
    initialize: noop,
    identify: noop,
    update: noop,
    track: noop,
    trackLink: noop,
    trackEnhancedEcommerce: noop,
    getHtml: noop,
    showHtml: noop,
    showBanner: noop,
    showWebLayer: noop,
    ping: noop,
    getAbTest: noop,
    loadDependency: noop,
    getRecommendation: noop,
    reloadWebLayers: noop,
    start: noop,
    notifications: {
        isAvailable: noop,
        isSubscribed: noop,
        subscribe: noop,
        unsubscribe: noop
    }
})

const ensureWebxpClientContract = () => {
    if (typeof window.webxpClient !== 'object' || window.webxpClient === null) {
        window.webxpClient = {}
    }

    if (typeof window.webxpClient.reset !== 'function') {
        window.webxpClient.reset = noop
    }
}

const warnExponeaFailure = (methodName: string, error: unknown) => {
    console.warn(`[Exponea] ${methodName} failed:`, error)
}

const createSafeExponeaClient = (getSdk: () => Record<string, any> | null) => {
    const safeClient = {} as Record<string, any>

    safeClient.getSDK = () => getSdk()

    const invokeSdkMethod = (methodName: string, args: unknown[]) => {
        const sdk = getSdk()
        const sdkMethod = sdk?.[methodName]

        if (typeof sdkMethod !== 'function') {
            return undefined
        }

        try {
            return Reflect.apply(sdkMethod, sdk, args)
        } catch (error) {
            warnExponeaFailure(methodName, error)
            return undefined
        }
    }

    const invokeNotificationMethod = (methodName: ExponeaNotificationMethodName, args: unknown[]) => {
        const sdk = getSdk()
        const notifications = sdk?.notifications
        const notificationMethod = notifications?.[methodName]

        if (typeof notificationMethod !== 'function') {
            return undefined
        }

        try {
            return Reflect.apply(notificationMethod, notifications, args)
        } catch (error) {
            warnExponeaFailure(`notifications.${methodName}`, error)
            return undefined
        }
    }

    for (const methodName of EXPONEA_METHODS) {
        safeClient[methodName] = (...args: unknown[]) => invokeSdkMethod(methodName, args)
    }

    safeClient.notifications = {}

    for (const methodName of EXPONEA_NOTIFICATION_METHODS) {
        safeClient.notifications[methodName] = (...args: unknown[]) => invokeNotificationMethod(methodName, args)
    }

    return safeClient
}

export default defineNuxtPlugin((nuxtApp) => {
    if (!import.meta.client) {
        return
    }

    const {
        public: { USE_EXPONEA = 'true', FIREBASE_PROJECT_ID = '', EXPONEA_PROJECT_TOKEN = '' }
    } = useRuntimeConfig()

    const isEnabledByFlag =
        typeof USE_EXPONEA === 'string' ? USE_EXPONEA.toLowerCase() !== 'false' : USE_EXPONEA !== false
    const token = typeof EXPONEA_PROJECT_TOKEN === 'string' ? EXPONEA_PROJECT_TOKEN.trim() : ''
    const runtimeEnvironment = getRuntimeEnvironmentFromProjectId(FIREBASE_PROJECT_ID)
    const hostname = window.location.hostname
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')
    const shouldInitializeExponea =
        isEnabledByFlag && isSupportedRuntimeEnvironment(runtimeEnvironment) && token.length > 0 && !isLocalHost
    const safeExponeaClient = createSafeExponeaClient(() => window.exponea ?? null)

    if (!shouldInitializeExponea) {
        const exponeaStub = createExponeaStub()
        ensureWebxpClientContract()
        window.exponea = exponeaStub
        window.$exponea = safeExponeaClient
        nuxtApp.provide('exponea', safeExponeaClient)
        return
    }

    /* eslint-disable */

    // @ts-ignore
    !(function (e: string, n: string, t: number, i: string, r: Document, a: ExponeaWindow, c: ExponeaBootstrapConfig) {
        function o(methods: string[]): ExponeaQueue {
            return methods.reduce<ExponeaQueue>(
                function (queue, method) {
                    return (
                        (queue[method] = function () {
                            queue._.push([method.toString(), arguments])
                        }),
                        queue
                    )
                },
                { _: [] }
            )
        }
        ;(c.target = c.target || 'https://api.exponea.com'),
            (c.file_path = c.file_path || c.target + '/js/' + n + '.min.js'),
            (a[n] = o([
                'anonymize',
                'initialize',
                'identify',
                'update',
                'track',
                'trackLink',
                'trackEnhancedEcommerce',
                'getHtml',
                'showHtml',
                'showBanner',
                'showWebLayer',
                'ping',
                'getAbTest',
                'loadDependency',
                'getRecommendation',
                'reloadWebLayers'
            ])),
            (a[n].notifications = o(['isAvailable', 'isSubscribed', 'subscribe', 'unsubscribe'])),
            (a[i] = {
                sdk: a[n],
                sdkObjectName: n,
                skipExperiments: !!c.new_experiments,
                sign: c.token + '/' + ((/__exponea_etc__=([\w-]+)/.exec(r.cookie) || ['', 'new'])[1] || 'new'),
                path: c.target
            }),
            (function (config: ExponeaBootstrapConfig, _unused: number, documentRef: Document, tagName: string) {
                const scriptTag = documentRef.createElement(tagName) as HTMLScriptElement
                scriptTag.type = 'text/java' + tagName
                scriptTag.async = true
                scriptTag.src = config.file_path || ''
                const firstTag = documentRef.getElementsByTagName(tagName)[0]
                if (firstTag?.parentNode) {
                    firstTag.parentNode.insertBefore(scriptTag, firstTag)
                }
            })(c, t, r, e),
            c.new_experiments &&
                (function (
                    config: ExponeaBootstrapConfig,
                    clientName: string,
                    targetWindow: ExponeaWindow,
                    documentRef: Document,
                    tagName: string
                ) {
                    const referrer = encodeURIComponent(location.href.split('#')[0] || '')
                    const client = targetWindow[clientName] as Record<string, any>
                    const modificationsUrl =
                        config.target +
                        '/webxp/' +
                        tagName +
                        '/' +
                        client.sign +
                        '/modifications.min.js?http-referrer=' +
                        referrer
                    client[tagName] =
                        '<' +
                        tagName +
                        ' type="text/java' +
                        tagName +
                        '" src="' +
                        modificationsUrl +
                        '"></' +
                        tagName +
                        '>'
                    if (config.non_flickering_blocking_script) {
                        documentRef.writeln(String(client[tagName]))
                    }
                    documentRef.writeln(
                        '<' +
                            tagName +
                            '>(!' +
                            clientName +
                            '.init || ' +
                            !config.non_flickering_blocking_script +
                            ') && document.writeln(' +
                            clientName +
                            '.' +
                            tagName +
                            '.replace("/' +
                            tagName +
                            '/", "/' +
                            tagName +
                            '-async/").replace("><", " async><"))</' +
                            tagName +
                            '>'
                    )
                })(c, i, a, r, e),
            (a[n].start = function (configOverrides?: Partial<ExponeaBootstrapConfig>) {
                return (
                    configOverrides &&
                        Object.keys(configOverrides).forEach(function (key: string) {
                            c[key] = configOverrides[key]
                        }),
                    a[n].initialize(c),
                    a[n]
                )
            })
    })('script', 'exponea', 0, 'webxpClient', document, window, {
        target: 'https://api.exponea.com',
        token,
        // replace with current customer ID or leave commented out for an anonymous customer
        // customer: window.currentUserId,
        track: {
            visits: true
        }
    })

    /* eslint-enable */

    // Ensure the WebXP bridge always exposes the reset hook expected by downstream scripts.
    ensureWebxpClientContract()

    // Initialize Exponea
    window.$exponea = safeExponeaClient
    safeExponeaClient.start()

    // Provide Exponea to the app
    nuxtApp.provide('exponea', safeExponeaClient)
})
